/**
 * Auth endpoints. Shared, because a divergence between how the two apps sign in is a divergence
 * in who is allowed to do what.
 */

import { api, getChannel } from '../api.js';
import { deviceLabel, platform } from '../../auth/device.js';
import type { CurrentUser, LoginRequest, PinUnlockRequest, Session } from '../../types/api.js';

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<Session, LoginRequest>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        // The label, platform and channel are filled here rather than at each call site, so
        // every sign-in in both apps produces the same shape in the owner's device list — and so
        // the channel can never be a call site's choice to omit.
        body: {
          ...body,
          channel: body.channel ?? getChannel(),
          deviceLabel: body.deviceLabel ?? deviceLabel(),
          platform: body.platform ?? platform(),
        },
      }),
      invalidatesTags: ['Session'],
    }),

    /**
     * The refresh token rides as an HttpOnly cookie — see `docs/api-requirements.md` §A2. The
     * body is six digits and nothing else, which is why this request is safe to make from a
     * screen that is showing a numeric keypad to a room.
     */
    pinUnlock: build.mutation<Session, PinUnlockRequest>({
      query: (body) => ({ url: '/auth/pin-unlock', method: 'POST', body }),
      invalidatesTags: ['Session'],
    }),

    /**
     * The dashboard's post-refresh "Continue as {name}?" step. Gated on the refresh cookie
     * alone, no PIN, no password — deliberately weaker than a silent renew. See
     * `ContinueSessionCommand` on the server for exactly what that trades away. The POS must
     * never call this; it has a PIN precisely so a browser-alone click cannot regain access.
     */
    continueSession: build.mutation<Session, void>({
      query: () => ({ url: '/auth/continue', method: 'POST' }),
      invalidatesTags: ['Session'],
    }),

    /**
     * Renews the session now, through the base query's single in-flight renew. Sent while the
     * person is clicking and typing on a screen that has not called the server for a while — see
     * `useKeepSessionAlive`. Carries no tags: nothing on screen changes.
     */
    keepSessionAlive: build.mutation<CurrentUser, void>({
      query: () => ({ url: '/auth/me', renewFirst: true }),
    }),

    logout: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),

    currentUser: build.query<CurrentUser, void>({
      query: () => '/auth/me',
      providesTags: ['Session'],
    }),

    /**
     * "Is this my password?" — the set-PIN sheet asks on Continue, so a wrong password is caught
     * before the PIN is typed twice. Counts toward the wrong-password lock like any other check.
     */
    checkOwnPassword: build.mutation<void, { password: string }>({
      query: (body) => ({ url: '/auth/me/password/check', method: 'POST', body }),
    }),

    setPin: build.mutation<void, { currentPassword: string; pin: string | null }>({
      query: (body) => ({ url: '/auth/me/pin', method: 'POST', body }),
      invalidatesTags: ['Session'],
    }),

    /** Finished or skipped the first-sign-in tour. The session is updated locally by `tourSeen`. */
    markTourSeen: build.mutation<void, 'dashboard' | 'pos'>({
      query: (app) => ({ url: `/auth/me/tour/${app}`, method: 'POST' }),
    }),

    changePassword: build.mutation<void, { currentPassword: string; newPassword: string }>({
      query: (body) => ({ url: '/auth/me/password', method: 'POST', body }),
    }),
  }),
});

export const {
  useLoginMutation,
  usePinUnlockMutation,
  useContinueSessionMutation,
  useKeepSessionAliveMutation,
  useLogoutMutation,
  useCurrentUserQuery,
  useCheckOwnPasswordMutation,
  useSetPinMutation,
  useChangePasswordMutation,
  useMarkTourSeenMutation,
} = authApi;
